// src/app/App.tsx: the app shell. Error boundary, boot states, the demo's bar, the current screen, the install card's
// slot (src/pwa/InstallSlot.tsx: empty until a card is due), the demo's captured-email sheet (src/demo/DemoMail.tsx,
// demo only), the test ride's sample library (src/try/SampleLibrary.tsx, test ride only) and the one toast host (spec
// 2.7, 2.9). On the public test ride a browser without its own copy sees the landing on every address but /privacy. Layouts (the prospect and team shells in src/ui/shell) are rendered by the screens themselves,
// because they need each screen's data.
import { Component, type ComponentChildren } from "preact";
import { DemoMailHost } from "../demo/DemoMail";
import { InstallSlot } from "../pwa/InstallSlot";
import { bootState, me, site } from "../state/session";
import { SampleLibraryHost } from "../try/SampleLibrary";
import { Button } from "../ui/Button";
import { HandoffWait, setupHandoff } from "../platform/handoff";
import { QuietSpinner } from "../ui/Feedback";
import { DemoBanner } from "../ui/shell/DemoStrip";
import { ToastHost } from "../ui/Toasts";
import { lazyScreen } from "./lazy";
import { OpeningSoonScreen, PausedScreen, UnknownHost } from "./NotFound";
import { route, type RouteName } from "./router";
import { SCREENS } from "./routes";

const TryLanding = lazyScreen(() => import("../try/TryLanding").then((m) => m.TryLanding));

type BoundaryState = { failed: boolean };

function ReloadScreen(props: { title: string; line?: string }) {
  return (
    <main class="hb-error" role="alert">
      <h1>{props.title}</h1>
      {props.line ? <p>{props.line}</p> : null}
      <p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </p>
    </main>
  );
}

/** Never a blank page: any render error shows a short message and a Reload button. */
export class ErrorBoundary extends Component<{ children?: ComponentChildren }, BoundaryState> {
  override state: BoundaryState = { failed: false };
  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }
  override componentDidCatch(error: unknown): void {
    console.error("render_error", error instanceof Error ? error.message : String(error));
  }
  override render() {
    if (this.state.failed) return <ReloadScreen title="Something went wrong" line="Reload the page to try again." />;
    return this.props.children;
  }
}

export { DemoBanner };

/**
 * The pages a builder's address shows while it is in setup or paused (docs/DECISIONS.md "Builders in setup"): the team's
 * own pages and sign-in. Anything else reads as an unknown host, as the API answers there.
 */
const SETUP_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>([
  "signin", "continue", "team", "teamProject", "teamProjectTab", "teamIdeas", "teamHomes", "teamStartYourBuild", "teamSettings", "teamPipeline", "teamMessages", "teamSetup", "teamActivate", "teamActivateDone",
  // A setup reminder's "Stop these emails" page (auth/UnsubscribeScreen.tsx).
  "unsubscribe",
]);

const BOARD_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>(["home", "rooms", "room", "item", "unsorted", "sort", "messages", "people", "settings", "choices"]);

export function App() {
  const state = bootState.value;
  // No spinner in a quick start (a sign-in link's hand-off goes from white to the workspace); a slow one shows one.
  if (state === "loading") return setupHandoff ? <HandoffWait /> : <QuietSpinner />;
  if (state === "unknown-host") return <UnknownHost />;
  if (state === "error") return <ReloadScreen title="We couldn't load this page" />;
  if (site.value && site.value.status !== "live" && !SETUP_ROUTES.has(route.value.name)) {
    // A builder in preview: its Start Your Build opens for its own signed-in team (a test family); everyone else sees "Opening soon".
    // The same team reads a test family's board as the family sees it (the API opens only the sample and test families to it).
    // Its sent page too (a test that makes no board), and a mistyped /team/... path reads as the workspace's own not-found.
    const name = route.value.name;
    const testing =
      site.value.status === "setup" && !!me.value?.staff &&
      (name === "start" || name === "startSent" || BOARD_ROUTES.has(name) || (name === "notFound" && window.location.pathname.startsWith("/team/")));
    if (!testing) return site.value.status === "paused" ? <PausedScreen /> : <OpeningSoonScreen />;
  }
  const trial = site.value?.trial;
  if (trial && !trial.copy && route.value.name !== "privacy") {
    return (
      <>
        <ErrorBoundary>
          <TryLanding />
        </ErrorBoundary>
        <ToastHost />
      </>
    );
  }
  const Screen = SCREENS[route.value.name];
  return (
    <>
      <ErrorBoundary>
        <DemoBanner />
        <Screen />
      </ErrorBoundary>
      <InstallSlot />
      <DemoMailHost />
      <SampleLibraryHost />
      <ToastHost />
    </>
  );
}
